import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { listStudioAssets } from "../../data/client";
import type { Asset } from "../../data/types";
import { AssetArt, Button, Display, PipMeter, Sprite, StatusChip } from "../../design/primitives";
import { art, cardSprite } from "../../design/sprites";
import { formatIcon } from "../../design/formatIcon";
import { artCandidates } from "../../library/coverUrl";
import { useStudio } from "./StudioContext";
import { EmptyState, ErrorState, LoadingState, NoResultsState } from "../states";
import css from "./studio.module.css";

/**
 * The asset table - §11.1 screen 3.
 *
 * Everything, drafts included. This is the owner's view, and the only place a
 * draft is ever visible.
 */

type Filter = "all" | "published" | "draft";

type Load =
  | { phase: "loading" }
  | { phase: "error"; error: Error }
  | { phase: "ready"; assets: Asset[] };

export default function Assets() {
  const { creator } = useStudio();
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * The confirmation a publish carried here from the form (FR-11).
   *
   * Read once into state and then scrubbed out of history, because location
   * state survives a reload: without the scrub, refreshing the list a day
   * later would announce a publish that happened a day ago.
   */
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null
  );
  useEffect(() => {
    if (!notice) return;
    navigate(location.pathname, { replace: true, state: null });
    // Once, on arrival. `notice` already holds the value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [load, setLoad] = useState<Load>({ phase: "loading" });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoad({ phase: "loading" });
    listStudioAssets()
      .then((assets) => live && setLoad({ phase: "ready", assets }))
      .catch((error: Error) => live && setLoad({ phase: "error", error }));
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const all = load.phase === "ready" ? load.assets : [];

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((a) => {
      if (filter !== "all" && a.status !== filter) return false;
      if (!q) return true;
      const game = creator.games.find((g) => g.id === a.gameId);
      // §13.1: search covers titles, games, tags - and drafts.
      const tags = a.tags
        .map((id) => creator.tags.find((t) => t.id === id)?.label ?? "")
        .join(" ");
      return `${a.title} ${game?.name ?? ""} ${tags}`.toLowerCase().includes(q);
    });
  }, [all, query, filter, creator]);

  /*
   * "+ NEW ASSET", not "+ NEW TAB" (FR-6, D-33, superseding D-15).
   *
   * Deriving this from the first non-retired type was wrong twice over: the
   * button creates an asset of no particular type yet - the switcher inside the
   * form decides that - and "first non-retired" is an arbitrary choice among
   * types the author may not have meant.
   */
  const newLabel = "+ NEW ASSET";

  if (load.phase === "loading") return <LoadingState label="LOADING" subLabel="FETCHING YOUR ASSETS" />;
  if (load.phase === "error") {
    return (
      <ErrorState
        headline="THE ASSET LIST DID NOT LOAD"
        detail="Nothing came back from the server. Reloading usually settles it."
        code={`STUDIO_ASSETS_FAILED · ${load.error.name.toUpperCase()}`}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <>
      <div className={css.head}>
        <Display size={38} as="h1">ASSETS</Display>
        {/* §11.2: a teal mono line, never a toast. It is dismissed by doing
            something else, which is why nothing here dismisses it. */}
        {notice && (
          <span className={css.confirm} role="status">
            {notice}
          </span>
        )}
      </div>

      <div className={css.toolbar}>
        <input
          className={css.input}
          style={{ maxWidth: 380 }}
          placeholder="Search titles, games and tags"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search assets"
        />
        <div className={css.segment}>
          {(["all", "published", "draft"] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              className={`${css.segmentButton} ${filter === f ? css.segmentActive : ""}`}
              onClick={() => setFilter(f)}
            >
              {f === "draft" ? "DRAFTS" : f.toUpperCase()}
            </button>
          ))}
        </div>
        <div className={css.toolbarRight}>
          <span className={css.label}>
            {shown.length} OF {all.length}
          </span>
          <Button variant="gold" size="rail" onClick={() => navigate("/studio/assets/new")}>
            {newLabel}
          </Button>
        </div>
      </div>

      {all.length === 0 ? (
        /*
         * §13.1 first run, in the shared state geometry (FR-33). This was
         * hand-rolled with a 150px sprite and its own padding, which is how
         * two surfaces stop looking like one system.
         */
        <EmptyState
          sprite={art.qblock}
          headline="NOTHING IN HERE YET"
          detail="Add an arrangement, attach its files and choose which tier can reach them. Publishing puts it in the library straight away."
          actionLabel={newLabel}
          onAction={() => navigate("/studio/assets/new")}
        />
      ) : (
        <>
          <div className={css.assetHead}>
            <span />
            <span className={css.label}>TITLE</span>
            <span className={css.label}>GAME</span>
            <span className={css.label}>DIFFICULTY</span>
            <span className={css.label}>FILES</span>
            <span className={css.label}>TIER</span>
            <span className={css.label}>STATUS</span>
          </div>

          {shown.map((asset) => {
            const game = creator.games.find((g) => g.id === asset.gameId);
            const typeDef = creator.assetTypes.find((t) => t.type === asset.assetType);
            const tier = creator.tiers.find((t) => t.id === asset.minTier);
            return (
              <Link key={asset.id} to={`/studio/assets/${asset.id}`} className={css.assetRow}>
                <AssetArt
                  coverUrls={artCandidates(asset, game, creator.publicAssetsBaseUrl)}
                  spriteSrc={cardSprite(typeDef?.cardObject ?? "SHEET", game?.tint ?? "#8A7FB0")}
                  size={44}
                />
                <span className={css.assetTitle}>{asset.title || "Untitled"}</span>
                <span className={css.assetGame}>
                  {game?.name ?? "—"}
                  {game?.retired && " · RETIRED"}
                </span>
                <PipMeter value={Number(asset.fields.difficulty ?? 0)} size={10} />
                <span className={css.fileIcons}>
                  {(typeDef?.slots ?? [])
                    .filter((s) => s.shortName)
                    .map((slot) => {
                      const icon = formatIcon(slot.accept);
                      return (
                        // §11.1: present at full opacity, missing at .25.
                        <span
                          key={slot.id}
                          className={asset.files[slot.id] ? undefined : css.fileMissing}
                          title={slot.label}
                        >
                          {/*
                            This column is icons only, so a format with no
                            sprite would vanish; it shows its short name
                            instead (FS-D11).
                          */}
                          {icon ? (
                            <Sprite src={icon} size={16} label={slot.shortName} />
                          ) : (
                            <span className={css.fileText}>{slot.shortName}</span>
                          )}
                        </span>
                      );
                    })}
                </span>
                <span className={css.tierCol}>{tier?.name ?? "—"}</span>
                <StatusChip status={asset.status === "published" ? "published" : "draft"} />
              </Link>
            );
          })}

          {shown.length === 0 && (
            // The patron's own no-results state. Studio had a near-copy at a
            // different type size; a near-copy is worse than a shared one,
            // because it drifts silently.
            <NoResultsState
              query={query}
              onClear={() => {
                setQuery("");
                setFilter("all");
              }}
            />
          )}
        </>
      )}
    </>
  );
}
